import { PC_KEYWORDS_TH_TOP, PC_KEYWORDS_TH_TOP_TOTAL } from "@/content/police-clearance-corpus/top.gen";
import { createFileRoute } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { PageGallery } from "@/components/PageGallery";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";

import { PC_FAQ_CATEGORIES } from "@/content/police-clearance-corpus/faq-defs";
import { getActiveProvince } from "@/provinces/_registry";

export const Route = createFileRoute("/police-clearance/faq/")({
  loader: async (): Promise<{ PC_FAQ_TH: { q: string; a: string }[]; PC_KEYWORDS_TH: string[] }> => {
    const [m0] = await Promise.all([
      import("@/content/police-clearance-corpus"),
    ]);
    return { PC_FAQ_TH: m0.PC_FAQ_TH_FULL, PC_KEYWORDS_TH: m0.PC_KEYWORDS_TH };
  },
  component: PcFaqHub,
  head: ({ loaderData }) => {
    const { PC_FAQ_TH = [], PC_KEYWORDS_TH = [] } = loaderData ?? {};

    const { config } = getActiveProvince();
    const site = getActiveSiteUrl();
    const title = `ขอใบตรวจประวัติอาชญากรรม FAQ — 100+ คำถามยอดฮิต 2026 | ${config.nameTh}`;
    const description = `รวมคำถามที่พบบ่อยเกี่ยวกับใบรับรองความประพฤติ / Police Clearance Certificate จากสำนักงานตำรวจแห่งชาติ ครอบคลุมค่าธรรมเนียม ขั้นตอน POA Apostille MFA และวีซ่า อัปเดต 2026`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: "/police-clearance/faq",
        type: "article",
        alternates: [{ lang: "en", path: "/en/police-clearance/faq" }],
        keywords: PC_KEYWORDS_TH_TOP.slice(0, 80),
      }),
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildBreadcrumbSchema([
          { name: "หน้าแรก", url: site },
          { name: "Police Clearance FAQ", url: `${site}/police-clearance/faq` },
        ])) },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(PC_FAQ_TH)) },
        { type: "application/ld+json", children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          speakable: { "@type": "SpeakableSpecification", cssSelector: ["h1", "h2", ".faq-answer"] },
          keywords: PC_KEYWORDS_TH_TOP.slice(0, 100).join(", "),
        }) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/police-clearance/faq")) },
      ],
    };
  },
});

function PcFaqHub() {
  const { PC_FAQ_TH, PC_KEYWORDS_TH } = Route.useLoaderData() as { PC_FAQ_TH: { q: string; a: string }[]; PC_KEYWORDS_TH: string[] };
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/" className="hover:underline">หน้าแรก</a> <span>/</span>{" "}
          <a href="/police-clearance" className="hover:underline">Police Clearance</a> <span>/</span>{" "}
          <span>FAQ</span>
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold mb-3">
          ขอใบตรวจประวัติอาชญากรรม FAQ — {PC_FAQ_TH.length}+ ข้อ (2026)
        </h1>
        <p className="text-muted-foreground mb-8">
          รวมคำถามที่พบบ่อยเกี่ยวกับใบรับรองความประพฤติ (Police Clearance Certificate) จากสำนักงานตำรวจแห่งชาติ —
          ครอบคลุม PCSC ปทุมวัน, POA จากต่างประเทศ, ค่าธรรมเนียม, Apostille 2026, และข้อกำหนดตามประเทศปลายทาง
        </p>

        <div className="mb-6 rounded-lg border bg-muted/30 p-4 text-sm">
          <strong>English:</strong>{" "}
          <a href="/en/police-clearance/faq" className="text-primary underline">
            English version — 100 FAQs for expats →
          </a>
        </div>

        <section aria-label="Police Clearance FAQ categories" className="mb-8">
          <h2 className="text-lg font-semibold mb-3">หมวดคำถาม</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {PC_FAQ_CATEGORIES.map((c) => (
              <a
                key={c.slug}
                href={`/police-clearance/faq/${c.slug}`}
                className="rounded-lg border bg-card p-3 text-sm transition-colors hover:border-primary hover:bg-primary/5"
              >
                <div className="font-semibold mb-1">{c.titleTh} →</div>
                <div className="text-xs text-muted-foreground line-clamp-2">{c.descTh}</div>
              </a>
            ))}
          </div>
        </section>

        <div className="space-y-4">
          {PC_FAQ_TH.map((item, i) => (
            <details key={i} className="rounded-lg border bg-card p-4 open:shadow-sm" {...(i < 5 ? { open: true } : {})}>
              <summary className="cursor-pointer font-semibold">
                <span className="text-muted-foreground mr-2">Q{i + 1}.</span>{item.q}
              </summary>
              <p className="faq-answer mt-3 text-sm leading-relaxed text-muted-foreground">{item.a}</p>
            </details>
          ))}
        </div>

        <section className="mt-12 rounded-lg border p-6 bg-primary/5">
          <h2 className="text-xl font-bold mb-2">ต้องการบริการเดินเรื่องแบบครบวงจร?</h2>
          <p className="text-sm text-muted-foreground mb-4">
            ปรึกษาฟรีกับทีมทนายผู้รับมอบอำนาจ — พิมพ์ลายนิ้วมือ + PCSC + แปล + MFA/Apostille + DHL ครบใน 10–15 วัน
          </p>
          <a href="/contact" className="inline-block rounded-md bg-primary px-5 py-2 text-primary-foreground font-medium">
            ติดต่อทีมงาน →
          </a>
        </section>

        <section className="mt-12">
          <h2 className="text-xl font-bold mb-3">หัวข้อยอดฮิต ({PC_KEYWORDS_TH_TOP_TOTAL} คำค้น)</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {PC_KEYWORDS_TH.map((kw, i) => (
              <span key={i} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground">{kw}</span>
            ))}
          </div>
        </section>
      
        <PageGallery category="background" seed="pc-faq-index" locale="th" />
        <FaqSourceNote topic="police-clearance" locale="th" />
      </main>
      <SiteFooter />
    </>
  );
}